import type { SearchOrganizationQuery, OrganizationsSearch } from '@repo/api-kit/graphql/types';
import { graphql } from '@repo/api-kit/graphql';
import { ClientError } from 'graphql-request';
import { graphQLClient } from '#utils/graphql-client';

export interface SearchOrganizationOptions {
  input?: OrganizationsSearch;
  includeFinancialDetail?: boolean;
  organizationId?: string;
}

const gqlQuery = graphql(`
  query searchOrganization($input: OrganizationsSearch, $includeFinancialDetail: Boolean!) {
    searchOrganization(input: $input) {
      organization {
        id
        name
        slug
        status
        createdAt
        suspensionReason
        avatar {
          url
          thumbUrl
          isDefault
        }
        company {
          legalName
          legalCountry
          legalForm
          legalNumber
          legalSector
          legalCode
          legalRegistrationDate
          legalTvaNumber
          tradeName
          contactEmail
          websiteUrl
          activityDescription
          addresses {
            type
            firstLine
            secondLine
            thirdLine
            zipCode
            city
            country
          }
          taxResidence {
            country
            taxIdentificationNumber
          }
          financialDetail @include(if: $includeFinancialDetail) {
            annualTurnover
            estimatedAnnualTurnover
            estimatedYearlyIncome
            maxSinglePaymentAmount
            yearlyTransactionVolume
          }
        }
        customer {
          contractSignedAt
          natureOfOperations
        }
        kyb {
          kybStatus
          flexKybStatus
          unreferenced
          ongoingKycKybUpdateProcessId
          typeformUrl
          associationTypeformUrl
          associationTypeformFileFilledAt
        }
        features {
          features
        }
        onboardingPartner {
          code
          type
        }
      }
    }
  }
`);

/**
 * Checks if an error is a 500-level server error that should be retried
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof ClientError) {
    const status = error.response.status;
    return status >= 500 && status < 600;
  }
  return false;
}

/**
 * Internal helper function to attempt the GraphQL request with retries
 */
async function attemptRequest(
  variables: { input?: OrganizationsSearch; includeFinancialDetail: boolean },
  organizationId: string | undefined,
  remainingRetries: number
): Promise<SearchOrganizationQuery['searchOrganization']['organization']> {
  try {
    const requestHeaders: Record<string, string> = {};
    if (organizationId) {
      requestHeaders['X-Qonto-Organization-Id'] = organizationId;
    }

    const result = await graphQLClient(gqlQuery, variables, { requestHeaders });
    return result.searchOrganization.organization;
  } catch (error) {
    // Only retry on 500-level errors and if we have retries remaining
    if (remainingRetries > 0 && isRetryableError(error)) {
      return attemptRequest(variables, organizationId, remainingRetries - 1);
    }

    // Not retryable or max retries reached, throw the error
    throw error;
  }
}

/**
 * @param options - Configuration object for the search query. Contains:
 *   - `input`: Optional GraphQL search input parameters
 *   - `includeFinancialDetail`: If true, includes financialDetail fields in the response (default: false)
 *   - `organizationId`: If provided, sets X-Qonto-Organization-Id header directly
 * @returns Promise resolving to the organization data
 */
export async function searchOrganization(
  options: SearchOrganizationOptions = {}
): Promise<SearchOrganizationQuery['searchOrganization']['organization']> {
  const variables = {
    input: options.input,
    includeFinancialDetail: options.includeFinancialDetail ?? false,
  };

  return attemptRequest(variables, options.organizationId, 2);
}
